import type { ColumnDef } from '@tanstack/react-table';
import LocationAccuracyTable from './LocationAccuracyTable';
import { accuracyColumn as col } from './locationAccuracyColumns';

type Block = { decisions: number; would_promote: number; skipped: number; promoted: number; failed: number;
  compared: number; awaiting_tap: number; never_tapped: number; median_abs_minutes: number | null;
  median_signed_minutes: number | null; within_5_pct: number | null; within_15_pct: number | null;
  tapped_later_15_pct: number | null; tapped_earlier_15_pct: number | null };
type EventRow = Block & { event: string };
type DriverRow = Block & { driver: string; single_dispatch: boolean; arrival_on: boolean; depart_on: boolean; top_skip_reason: string | null };
type SkipRow = { reason: string; decisions: number; compared: number; within_15_pct: number | null };
type Decision = { driver: string; pronumber: string; legnum: number; event: string; location: string | null;
  proposed: string | null; tapped: string | null; minutes: number | null; action: string; skip_reason: string | null;
  detail: string | null; ticks: number; first_seen: string; last_seen: string };
type Status = { mode: string; job_enabled: boolean | null; job_frequency: string | null; last_run_at: string | null;
  last_run_status: string | null; last_run_records: number | null; last_run_error: string | null;
  drivers_on: number; arrival_on: number; depart_on: number };
export type AutostampReview = { status: Status; since: string; truncated: boolean; decision_total: number;
  events: EventRow[]; drivers: DriverRow[]; skips: SkipRow[]; recent: Decision[] };

const actions: Record<string, string> = {
  would_promote: 'Would stamp', skipped: 'Skipped', promoted: 'Stamped', failed: 'Failed',
};
const reasons: Record<string, string> = {
  dispatch_not_started: 'Dispatch not started', geofence_unusable: 'Geofence unusable', stale: 'Older than 6 hours',
  future_dated: 'Future-dated', out_of_order: 'Out of order with leg times', bad_value: 'Unreadable GPS time',
  ambiguous_location: 'Two legs waiting at the same stop', deadline: 'Sweep ran out of time',
};
const reason = (key: string | null) => key ? reasons[key] ?? key.replace(/_/g, ' ') : '';
const yes = (value: boolean) => value ? 'Yes' : 'No';

function blockColumns<T extends Block>(): ColumnDef<T>[] {
  return [
    col<T>('decisions', 'Decisions'), col<T>('would_promote', 'Would stamp'),
    col<T>('compared', 'Compared with tap'), col<T>('within_5_pct', 'Within 5 min (%)'),
    col<T>('within_15_pct', 'Within 15 min (%)'), col<T>('median_signed_minutes', 'Median tap − GPS (min)'),
    col<T>('median_abs_minutes', 'Median gap (min)'), col<T>('tapped_later_15_pct', 'Driver over 15 min later (%)'),
    col<T>('tapped_earlier_15_pct', 'Driver over 15 min earlier (%)'), col<T>('awaiting_tap', 'Awaiting tap'),
    col<T>('never_tapped', 'Would fill (no tap after 6 h)'), col<T>('skipped', 'Skipped'),
    col<T>('promoted', 'Stamped'), col<T>('failed', 'Failed'),
  ];
}

export default function GeofenceAutostampReview({ review }: { review: AutostampReview | null | undefined }) {
  if (!review) return <section className="space-y-2"><h2 className="text-lg font-semibold">Geofence auto-stamp</h2>
    <p className="text-sm text-muted">The auto-stamp review could not be loaded. The comparison above is unaffected.</p></section>;
  const { status } = review;
  const steps = [
    status.job_enabled === false && 'Turn on the geofence_autostamp job in Sync Jobs.',
    status.drivers_on === 0 && 'Switch drivers on in Drivers › Preferences/Bans (Auto Arrival / Auto Depart).',
    status.mode === 'off' && 'Set the app setting geofence_autostamp.mode to shadow.',
  ].filter(Boolean) as string[];
  const eventColumns: ColumnDef<EventRow>[] = [col('event', 'Event'), ...blockColumns<EventRow>()];
  const driverColumns: ColumnDef<DriverRow>[] = [
    col('driver', 'Unit'), col('single_dispatch', 'Single dispatch', row => yes(row.single_dispatch)),
    col('arrival_on', 'Auto Arrival', row => yes(row.arrival_on)), col('depart_on', 'Auto Depart', row => yes(row.depart_on)),
    ...blockColumns<DriverRow>(), col('top_skip_reason', 'Most common skip', row => reason(row.top_skip_reason)),
  ];
  const skipColumns: ColumnDef<SkipRow>[] = [
    col('reason', 'Skip reason', row => reason(row.reason)), col('decisions', 'Decisions'),
    col('compared', 'With a driver tap'), col('within_15_pct', 'GPS within 15 min of tap (%)'),
  ];
  const decisionColumns: ColumnDef<Decision>[] = [
    col('driver', 'Unit'), col('pronumber', 'PRO / GPS', row => <a className="text-blue-600 underline"
      href={`/geofencing/driver-travel-history?pro=${encodeURIComponent(row.pronumber)}&legnum=${row.legnum}`}
      target="_blank" rel="noreferrer">{row.pronumber}-{row.legnum}</a>),
    col('event', 'Event'), col('location', 'Location'), col('proposed', 'GPS time'), col('tapped', 'Driver tap'),
    col('minutes', 'Tap − GPS (min)'), col('action', 'Decision', row => actions[row.action] ?? row.action),
    col('skip_reason', 'Skip reason', row => reason(row.skip_reason)), col('detail', 'Detail'),
    col('ticks', 'Sweeps'), col('first_seen', 'First seen'), col('last_seen', 'Last seen'),
  ];
  return <section className="space-y-3">
    <div>
      <h2 className="text-lg font-semibold">Geofence auto-stamp</h2>
      <p className="text-sm mt-1">
        Mode: <strong>{status.mode}</strong> · Sweep job: {status.job_enabled == null ? 'unknown' : status.job_enabled ? `on, every ${status.job_frequency}` : 'off'}
        {' '}· Last sweep: {status.last_run_at ?? '—'}{status.last_run_status ? ` (${status.last_run_status}, ${status.last_run_records ?? 0} records)` : ''}
        {' '}· Drivers switched on: {status.drivers_on} (arrival {status.arrival_on}, departure {status.depart_on})
      </p>
      {status.last_run_error && <p role="alert" className="text-red-600 text-sm">Last sweep error: {status.last_run_error}</p>}
      {status.mode === 'live' && <p className="text-sm font-medium">Live: eligible stamps are written to the legs through the normal order save.</p>}
      {steps.length > 0 && <div className="text-sm border rounded p-3 mt-2"><p className="font-medium">To start the shadow review:</p>
        <ul className="list-disc pl-6">{steps.map(step => <li key={step}>{step}</li>)}</ul></div>}
      <p className="text-sm text-muted mt-2">Decisions from {review.since} through now, including today. Each would-be stamp is compared with the time the driver tapped: tap minus GPS, so positive means the driver tapped later. In shadow nothing is written; a would-be stamp is fixed at its first decision, as live would have written it.{review.truncated ? ' Showing the newest 20,000 decisions.' : ''}</p>
    </div>
    <LocationAccuracyTable name="autostamp-events" title="By event" data={review.events} columns={eventColumns} />
    <LocationAccuracyTable name="autostamp-drivers" title={`By driver · ${review.drivers.length} drivers`} data={review.drivers} columns={driverColumns} />
    <LocationAccuracyTable name="autostamp-skips" title="Skip reasons" data={review.skips} columns={skipColumns} />
    <details><summary className="cursor-pointer font-semibold">Recent decisions · {Math.min(review.recent.length, 300)} of {review.decision_total}</summary>
      <p className="text-sm text-muted">Newest first. Driver/PRO search above applies here.</p>
      <LocationAccuracyTable name="autostamp-recent" title="Recent auto-stamp decisions" data={review.recent} columns={decisionColumns} />
    </details>
  </section>;
}
